import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TuneIcon from "@mui/icons-material/Tune";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import EmailPreview from "@/components/campaignBuilder/design/EmailPreview";
import ReminderDrawer from "@/components/campaignBuilder/reminder/ReminderDrawer";
import {
  RECIPIENTS_LOCKED_NOTICE,
  REMINDER_SCHEDULES,
  STEP_LOCKED_LABEL,
} from "@/constants/campaignBuilder";
import { DEFAULT_REMINDER_EMAIL, useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { isRecipientsLocked } from "@/utils/campaign-gating-helper";

interface ReminderNavState {
  reminderIndex?: number;
  mode?: "edit" | "preview";
}

/**
 * The lock wrapper must not disturb this page's flex column, so it is taken out of layout with
 * `display: contents` — `disabled` propagates to descendant form controls from the DOM tree, not
 * from the box tree, so the fieldset still does its job while contributing no box of its own.
 * The rest zeroes the UA chrome for the (benign) case where a browser ignores `display: contents`.
 */
const LOCK_FIELDSET_STYLE = {
  display: "contents",
  border: 0,
  margin: 0,
  padding: 0,
  minInlineSize: 0,
} as const;

export default function EmailReminderStation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { reminderIndex = 0, mode = "edit" } = (location.state as ReminderNavState) ?? {};

  const {
    stage3Email,
    reminderCount,
    reminderEmails,
    setReminderEmail,
    recipients,
    openSendTest,
    saveStepIfDirty,
    campaignStatus,
  } = useCampaignBuilder();
  /**
   * Reminders belong to the STEP 2 save-group, which the server refuses once the campaign leaves
   * draft — and this editor is reachable by direct URL (`/campaign/create/reminders`), so it has
   * to gate itself instead of relying on the Recipients step it is usually opened from.
   * `null` = CREATE mode ⇒ `false`: a brand-new campaign gets today's behaviour untouched.
   */
  const locked = isRecipientsLocked(campaignStatus);

  const schedule = REMINDER_SCHEDULES[reminderCount] ?? [];
  const currentItem = schedule[reminderIndex];
  const reminderState = reminderEmails[reminderIndex] ?? DEFAULT_REMINDER_EMAIL;

  const [drawerOpen, setDrawerOpen] = useState(mode === "edit");
  const [switchOpen, setSwitchOpen] = useState(false);
  const switchRef = useRef<HTMLDivElement>(null);

  // Close switch dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (switchRef.current && !switchRef.current.contains(e.target as Node)) {
        setSwitchOpen(false);
      }
    }
    if (switchOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [switchOpen]);

  // Merge branding from main email with reminder-specific overrides
  const previewState = {
    ...stage3Email,
    subject: reminderState.subject,
    messageHtml: reminderState.messageHtml,
    logoEnabled: stage3Email.logoEnabled && reminderState.showLogo,
    bannerEnabled: stage3Email.bannerEnabled && reminderState.showBanner,
  };

  function handleBack() {
    navigate(-1);
  }

  function handleSave() {
    // Reminders live in the Step 2 save-group. Persist them if they changed (dirty-compare
    // inside), then navigate back to Step 2 (Recipients).
    //
    // Locked: go back WITHOUT saving. This is the guard that matters — the button below is also
    // disabled, but a programmatic click must not be able to fire the one PATCH the server is
    // guaranteed to answer with 409.
    if (!locked) void saveStepIfDirty(2);
    navigate("/campaign/create/recipients");
  }

  function handleSwitchTo(index: number) {
    setSwitchOpen(false);
    navigate("/campaign/create/reminders", {
      state: { reminderIndex: index, mode: drawerOpen ? "edit" : "preview" },
      replace: true,
    });
  }

  const btnBase =
    "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-btn border font-lato text-[12px] font-semibold uppercase tracking-[0.8px] transition-all";
  const btnDefault =
    "border-smoke text-graphite bg-white hover:border-dark-dust hover:text-dark-dust";
  const btnActive = "border-rodeo-black text-rodeo-black bg-wax";

  return (
    <div className="fixed inset-0 z-9000 flex flex-col bg-wax">
      {/* ── Header bar ── */}
      <div
        className="relative flex items-center justify-between shrink-0 bg-white"
        style={{
          minHeight: 80,
          padding: "12px 24px",
          borderBottom: "1px solid var(--color-card-border)",
        }}
      >
        {/* Left: Back + Switch Message */}
        <div className="flex items-center gap-2 min-w-0">
          <button type="button" onClick={handleBack} className={`${btnBase} ${btnDefault}`}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            Back
          </button>

          <div className="relative" ref={switchRef}>
            <button
              type="button"
              onClick={() => setSwitchOpen((v) => !v)}
              className={`${btnBase} ${switchOpen ? btnActive : btnDefault}`}
              aria-haspopup="listbox"
              aria-expanded={switchOpen}
            >
              <SwapHorizIcon sx={{ fontSize: 16 }} />
              Switch Message
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  marginLeft: "-2px",
                  transition: "transform 0.15s",
                  transform: switchOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </button>

            {switchOpen && (
              <div
                className="absolute left-0 bg-white"
                style={{
                  top: "calc(100% + 6px)",
                  minWidth: 280,
                  border: "1px solid var(--color-smoke)",
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  padding: "6px 0",
                  zIndex: 300,
                }}
                role="listbox"
              >
                {schedule.map((item, idx) => {
                  const isActive = idx === reminderIndex;
                  return (
                    <button
                      key={item.label}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onClick={() => handleSwitchTo(idx)}
                      className={`w-full flex flex-col text-left transition-colors hover:bg-clear-dust ${isActive ? "bg-clear-dust" : ""}`}
                      style={{ padding: "10px 14px" }}
                    >
                      <span
                        className="font-lato text-[13px] font-semibold"
                        style={{
                          color: isActive ? "var(--color-dark-dust)" : "var(--color-rodeo-black)",
                        }}
                      >
                        {item.label}
                      </span>
                      <span
                        className="font-lato text-[11px] text-graphite"
                        style={{ marginTop: 2 }}
                      >
                        {item.timing}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Center: Title + subtitle */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none"
          style={{ maxWidth: "calc(100% - 600px)" }}
        >
          <p
            className="font-crimson text-[20px] text-rodeo-black overflow-hidden"
            style={{ lineHeight: 1.2, whiteSpace: "nowrap", textOverflow: "ellipsis" }}
          >
            {currentItem?.label ?? "Reminder Email"}
          </p>
          {currentItem?.timing && (
            <p className="font-lato text-[12px] text-graphite" style={{ marginTop: 2 }}>
              {currentItem.timing}
            </p>
          )}
        </div>

        {/* Right: Send Test + Customize + Save */}
        <div className="flex items-center gap-2">
          <button type="button" onClick={openSendTest} className={`${btnBase} ${btnDefault}`}>
            <SendOutlinedIcon sx={{ fontSize: 14 }} />
            Send Test
          </button>
          {/* Customize opens the EDIT surface, so it is the one header control the lock closes —
              see the drawer's `open` prop below for why nothing becomes unreadable. */}
          <button
            type="button"
            onClick={() => setDrawerOpen((v) => !v)}
            disabled={locked}
            title={locked ? STEP_LOCKED_LABEL : undefined}
            className={`${btnBase} ${drawerOpen && !locked ? btnActive : btnDefault} ${
              locked ? "opacity-55 cursor-not-allowed" : ""
            }`}
          >
            <TuneIcon sx={{ fontSize: 16 }} />
            Customize
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={locked}
            title={locked ? STEP_LOCKED_LABEL : undefined}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-btn font-lato text-[12px] font-semibold uppercase tracking-[0.8px] text-white transition-colors ${
              locked ? "opacity-55 cursor-not-allowed" : ""
            }`}
            style={{
              background: "var(--color-rodeo-black)",
              border: "1px solid var(--color-rodeo-black)",
            }}
          >
            Save
          </button>
        </div>
      </div>

      {/* Lock banner — a full-width strip under the header, OUTSIDE the disabled fieldset so it
          stays announceable. Same copy the server sends with its 409 for this save-group. */}
      {locked && (
        <div
          className="flex items-center gap-2 shrink-0 px-6 py-2.5"
          style={{
            background: "var(--color-light-dust)",
            borderBottom: "1px solid var(--color-smoke)",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 14, color: "var(--color-dark-dust)" }} />
          <p className="font-lato text-[12px] text-graphite leading-none">
            {RECIPIENTS_LOCKED_NOTICE}
          </p>
        </div>
      )}

      {/* ── Canvas area — relative so drawer positions inside it ── */}
      <div className="relative flex-1 overflow-hidden flex flex-col">
        <fieldset disabled={locked} style={LOCK_FIELDSET_STYLE}>
          {/* The preview is this page's READ surface — it renders the real subject and body — so
              it is deliberately NOT dimmed: there is nothing interactive in it to signal as inert,
              and fading it would only make the content the sender came to read harder to read. */}
          <div className="flex-1 overflow-y-auto px-5 py-5 flex justify-center items-start bg-wax">
            <EmailPreview state={previewState} isMobile={false} recipients={recipients} />
          </div>

          {/* Locked ⇒ the drawer does not mount at all, rather than mounting inert. Its message
              body is a Slate `<Editable>` (a contenteditable), and `fieldset disabled` does NOT
              disable contenteditable — it only reaches form controls — so an inert-looking drawer
              would still be typeable by keyboard, mutating the step-2 reminder state. Nothing is
              hidden by this: the subject, body, logo and banner all render in the preview above. */}
          <ReminderDrawer
            open={drawerOpen && !locked}
            onClose={() => setDrawerOpen(false)}
            state={reminderState}
            onChange={(v) => setReminderEmail(reminderIndex, v)}
          />
        </fieldset>
      </div>
    </div>
  );
}
